import { AppErrorBoundary } from 'components/AppErrorBoundary';
import { ErrorFallback } from 'components/AppErrorBoundary/ErrorFallback';
import { AuthedLayout, AuthedOrBasicLayout, BasicLayout } from 'components/Layout';
import { AuthenticationLayout } from 'components/Layout/AuthenticationLayout';
import { ScrollToTop } from 'components/ScrollToTop';
import { TitleAnnouncer } from 'components/TitleAnnouncer';
import { AuthedGate } from 'domains/auth/AuthedGate';
import { AWSCognitoWrapper } from 'domains/auth/AWSCognitoWrapper/AWSCognitoWrapper';
import { InviteLinkLogin } from 'domains/auth/InviteLinkLogin/InviteLink';
import { ProfileSelectedGate } from 'domains/auth/ProfileSelectedGate';
import { UserClientsWrapper } from 'domains/auth/UserClientsWrapper/UserClientsWrapper';
import { OngoingSessionsGate } from 'domains/gates/OngoingSessionsGate';
import IntakeGate from 'domains/intake/IntakeGate/IntakeGate';
import { PreviewADL } from 'pages/Assignment/previews/ADL';
import { PreviewCustomStructuredActivity } from 'pages/Assignment/previews/CustomStructuredActivity';
import { PreviewLearningPath } from 'pages/Assignment/previews/LearningPath';
import { PreviewStructuredActivity } from 'pages/Assignment/previews/StructuredActivity';
import { Licenses } from 'pages/Licenses';
import { CarePlanPDF } from 'pages/PDFs/CarePlanPDF';
import { Reschedule } from 'pages/RescheduleDirect';
import Screener from 'pages/Screener';
import ScreenerResults from 'pages/Screener/Results';
import { ScreenerResultsPDF } from 'pages/Screener/Results/PDF';
import ScreenerSignup from 'pages/Screener/ScreenerSignup';
import { SuperbillPDF } from 'pages/Superbill/PDF';
import { Support } from 'pages/Support';
import { SupportCategory } from 'pages/Support/Category';
import { UserErrorPage } from 'pages/UserError';
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from 'react-router-dom';
import { AsyncRoute } from 'router/AsyncRoute';
import { routes } from 'router/routes';

import { OpenSupportCaseRedirect } from 'components/OpenSupportCase';
import { ScheduleWrapper } from 'domains/direct-schedule/ScheduleWrapper';
import {
  ConsultFormGate,
  EvalFormGate,
  EvalScheduleGate,
  FiltersGate,
  LandingPageGate,
  PickTherapistGateV2,
  SecondSplitGate,
  SecondSplitGateV2,
  SelfPayGate,
} from 'domains/signupDirect/context';
import { DirectSignupEnterFilters } from 'domains/signupDirect/EnterFiltersStep/EnterFilters';
import { DirectSignupEvaluationForm } from 'domains/signupDirect/Evaluation/EvaluationForm';
import { DirectSignupPickTimeSlot } from 'domains/signupDirect/Evaluation/Schedule/Single/SingleEvaluationScheduleSection';
import { DirectSignupScheduleConsultationForm } from 'domains/signupDirect/ScheduleConsultation/ScheduleConsultationForm';
import { Schedule } from 'pages/ScheduleDirect';
import ScheduleConfirmation from 'pages/ScheduleDirect/blocks/ScheduleConfirm';
import SignupDirect from 'pages/SignUpDirect/SignupDirect';
import { Navigate } from 'react-router-dom';
import NavigateWithParams from 'utils/routing/NavigateWithParams';
import SignupErrorBoundary from 'domains/signupDirect/misc/SignupErrorBoundary';
import DirectSignupResumeLeadStep from 'domains/signupDirect/ResumeLead/Components/ResumeLeadStep';
import { SplitEvaluationsScheduleSection } from 'domains/signupDirect/Evaluation/Schedule/Split/ByTherapist/SplitEvaluationsScheduleSection';
import { EvaluationScheduleFirstSplit } from 'domains/signupDirect/Evaluation/Schedule/Split/ByTherapist/FirstSplit/FirstSplit';
import { EvaluationScheduleSecondSplit } from 'domains/signupDirect/Evaluation/Schedule/Split/ByTherapist/SecondSplit/SecondSplit';
import { SignupScheduleLandingPage } from 'domains/signupDirect/Evaluation/Schedule/LandingPage/LandingPage';
import { SignupJotformRedirect } from 'domains/signupDirect/JotFormRedirect/JotFormRedirect';
import { SignupFormPageLayoutWithProgressBar } from 'components/Layout/SignupLayout/SignupFormPageLayoutWithProgressBar';
import { UnauthenticatedGate } from 'domains/auth/UnauthenticatedGate/UnauthenticatedGate';
import { SplitEvaluationsScheduleSectionV2 } from 'domains/signupDirect/Evaluation/Schedule/Split/BySlot/SplitEvaluationsScheduleSectionV2';
import { EvaluationScheduleFirstSplitV2 } from 'domains/signupDirect/Evaluation/Schedule/Split/BySlot/FirstSplit/FirstSplit';
import { EvaluationScheduleSecondSplitV2 } from 'domains/signupDirect/Evaluation/Schedule/Split/BySlot/SecondSplit/SecondSplit';
import { SignupSchedulingExperimentFork } from 'domains/signupDirect/Evaluation/Schedule/Split/SchedulingExperiment';
import { EvaluationSchedulePickTherapistV2 } from 'domains/signupDirect/Evaluation/Schedule/Split/BySlot/PickTherapist/PickTherapist';
import { SignupLayout } from 'components/Layout/SignupLayout/SignupLayout';
import { SignupContactGate } from 'components/Layout/SignupLayout/SignupContactGate';
import SelfPayPage from 'domains/signupDirect/SelfPay';
import { PostSignup } from 'domains/signupDirect/PostSignup/PostSignup';
import { LoginPages } from 'domains/auth/LoginPages/LoginPages';

export const appRoutes = createRoutesFromElements(
  <Route element={<AppErrorBoundary />}>
    <Route
      element={
        <>
          <TitleAnnouncer />
          <ScrollToTop />
          <Outlet />
        </>
      }
    >
      {/* Authenticated routes */}
      <Route element={<AWSCognitoWrapper />}>
        <Route element={<AppErrorBoundary />}>
          <Route element={<UserClientsWrapper />}>
            <Route element={<AuthedOrBasicLayout />}>
              <Route path={routes.support.base} element={<Support />} />
              <Route path={routes.support.category} element={<SupportCategory />} />
              <Route path={routes.licenses} element={<Licenses />} />
            </Route>

            <Route element={<AuthedGate />}>
              <Route element={<ProfileSelectedGate />}>
                <Route element={<AuthedLayout />}>
                  <Route element={<IntakeGate clientType="adult" />}>
                    <Route path={routes.intake.adult} element={<AsyncRoute />} />
                  </Route>

                  <Route element={<IntakeGate clientType="child" />}>
                    <Route path={routes.intake.child} element={<AsyncRoute />} />
                  </Route>

                  <Route path={routes.intake.completed} element={<AsyncRoute />} />

                  <Route path={routes.releaseOfInformation} element={<AsyncRoute />} />

                  <Route element={<OngoingSessionsGate />}>
                    <Route path={routes.root} element={<AsyncRoute />} />
                    <Route path={routes.carePlan.evaluationReport} element={<AsyncRoute />} />
                    <Route path={routes.session.detail} element={<AsyncRoute />} />
                    <Route path={routes.careHistory} element={<AsyncRoute />} />
                    <Route path={routes.resourceLibrary} element={<AsyncRoute />} />
                    <Route path={routes.billing} element={<AsyncRoute />} />
                    <Route path={routes.bookRecurringSession} element={<AsyncRoute />} />
                    <Route path={routes.confirmRecurringSession} element={<AsyncRoute />} />
                    <Route path={routes.billingUpdatePaymentMethod} element={<AsyncRoute />} />
                    <Route path={routes.claims} element={<AsyncRoute />} />
                    <Route path={routes.homePractice} element={<AsyncRoute />} />
                    <Route path={routes.assignments.detail} element={<AsyncRoute />} />
                    <Route path={routes.assignments.completed} element={<AsyncRoute />} />
                    <Route path={routes.rescheduleAppointment} element={<AsyncRoute />} />
                    <Route path={routes.changeInitialEvaluation.base} element={<AsyncRoute />} />
                    <Route path={routes.changeInitialEvaluation.selectTherapist} element={<AsyncRoute />} />
                    <Route path={routes.changeInitialEvaluation.confirm} element={<AsyncRoute />} />
                    <Route path={routes.fileUploadForm} element={<AsyncRoute />} />
                    <Route path={routes.cancelAppointment} element={<AsyncRoute />} />
                    <Route path={routes.changeAppointment} element={<AsyncRoute />} />
                  </Route>

                  <Route path={routes.openSupportCase} element={<OpenSupportCaseRedirect />} />
                </Route>
              </Route>
            </Route>

            <Route element={<SignupErrorBoundary />}>
              <Route element={<SignupLayout />}>
                <Route element={<SignupContactGate />}>
                  <Route element={<SignupDirect />}>
                    <Route path={routes.signupDirect.completed} element={<PostSignup />} />
                    <Route path={routes.signupDirect.base} element={<Navigate to={routes.signupDirect.filters} />} />

                    <Route element={<LandingPageGate />}>
                      <Route path={routes.signupDirect.landingPage} element={<SignupScheduleLandingPage />} />
                    </Route>

                    <Route element={<SignupFormPageLayoutWithProgressBar />}>
                      <Route element={<FiltersGate />}>
                        <Route path={routes.signupDirect.filters} element={<DirectSignupEnterFilters />} />
                      </Route>

                      <Route element={<EvalScheduleGate />}>
                        <Route
                          path={routes.signupDirect.evaluation.schedule.single}
                          element={<DirectSignupPickTimeSlot />}
                        />

                        <Route
                          element={<SignupSchedulingExperimentFork />}
                          path={routes.signupDirect.evaluation.schedule.split.experimentFork}
                        />

                        <Route element={<SplitEvaluationsScheduleSection />}>
                          <Route
                            path={routes.signupDirect.evaluation.schedule.split.first}
                            element={<EvaluationScheduleFirstSplit />}
                          />

                          <Route element={<SecondSplitGate />}>
                            <Route
                              path={routes.signupDirect.evaluation.schedule.split.second}
                              element={<EvaluationScheduleSecondSplit />}
                            />
                          </Route>
                        </Route>

                        <Route element={<SplitEvaluationsScheduleSectionV2 />}>
                          <Route
                            path={routes.signupDirect.evaluation.schedule.split.v2.first}
                            element={<EvaluationScheduleFirstSplitV2 />}
                          />

                          <Route element={<SecondSplitGateV2 />}>
                            <Route
                              path={routes.signupDirect.evaluation.schedule.split.v2.second}
                              element={<EvaluationScheduleSecondSplitV2 />}
                            />
                          </Route>

                          <Route element={<PickTherapistGateV2 />}>
                            <Route
                              path={routes.signupDirect.evaluation.schedule.split.v2.therapist}
                              element={<EvaluationSchedulePickTherapistV2 />}
                            />
                          </Route>
                        </Route>
                      </Route>

                      <Route element={<EvalFormGate />}>
                        <Route path={routes.signupDirect.evaluation.details} element={<DirectSignupEvaluationForm />} />
                      </Route>

                      <Route element={<ConsultFormGate />}>
                        <Route
                          path={routes.signupDirect.consultation.details}
                          element={<DirectSignupScheduleConsultationForm />}
                        />
                      </Route>

                      <Route path={routes.signupDirect.resume} element={<DirectSignupResumeLeadStep />} />

                      <Route path={routes.signupDirect.jotformRedirect} element={<SignupJotformRedirect />} />
                    </Route>
                    <Route element={<SelfPayGate />}>
                      <Route path={routes.signupDirect.offerPrivatePay} element={<SelfPayPage />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              <Route path={routes.signupV2} element={<NavigateWithParams to={routes.signupDirect.base} />} />
            </Route>
          </Route>

          <Route path="*" element={<ErrorFallback />} />
        </Route>

        <Route element={<AuthenticationLayout flashWrapper={false} />}>
          <Route element={<UnauthenticatedGate />}>
            <Route path={routes.resetPassword} element={<LoginPages.ResetPassword />} />
          </Route>

          <Route path={routes.invitation} element={<InviteLinkLogin />} />
        </Route>
      </Route>

      {/* Unauthenticated routes */}

      <Route element={<BasicLayout showLanguageSwitcher={false} />}>
        <Route path={routes.signupV1} element={<NavigateWithParams to={routes.signupDirect.base} />} />
        <Route path={routes.screener.base} element={<Screener />} />
        <Route path={routes.screener.results.base} element={<ScreenerResults />} />
        <Route path={routes.screener.signup} element={<ScreenerSignup />} />
      </Route>

      <Route element={<BasicLayout showLanguageSwitcher={true} />}>
        <Route path={routes.signupV3} element={<NavigateWithParams to={routes.signupDirect.base} />} />
      </Route>

      <Route element={<BasicLayout showLanguageSwitcher={true} />}>
        <Route path={routes.signupV4} element={<NavigateWithParams to={routes.signupDirect.base} />} />
      </Route>

      <Route path={routes.pdf.screener} element={<ScreenerResultsPDF />} />
      <Route path={routes.pdf.carePlan} element={<CarePlanPDF />} />
      <Route path={routes.pdf.superbill} element={<SuperbillPDF />} />

      <Route element={<BasicLayout />}>
        <Route path={routes.assignments.previews.learningPath} element={<PreviewLearningPath />} />
        <Route path={routes.assignments.previews.ADL} element={<PreviewADL />} />
        <Route path={routes.assignments.previews.structuredActivity} element={<PreviewStructuredActivity />} />
        <Route
          path={routes.assignments.previews.customStructuredActivity}
          element={<PreviewCustomStructuredActivity />}
        />
      </Route>

      <Route element={<BasicLayout />}>
        <Route path={routes.rescheduleDirect} element={<Reschedule />} />
      </Route>

      <Route element={<BasicLayout />}>
        <Route element={<ScheduleWrapper />}>
          <Route path={routes.scheduleDirect} element={<Schedule />} />
          <Route path={routes.confirmSchedule} element={<ScheduleConfirmation />} />
        </Route>
      </Route>

      <Route path={routes.userError} element={<UserErrorPage />} />
      <Route path={routes.rescheduleExpired} element={<AsyncRoute />} />
      <Route path={routes.rescheduleUnavailable} element={<AsyncRoute />} />

      <Route path="*" element={<ErrorFallback />} />
    </Route>
  </Route>,
);

const routerDefinition = createBrowserRouter(appRoutes);

export const Router = () => (
  <>
    <RouterProvider router={routerDefinition} />
  </>
);
export default Router;
